import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from "react";

import { getActiveSession, getActiveUser, signOut } from "../../lib/auth";
import { supabase } from "../../lib/supabase";
import type { PresencePayload, MovementPayload, UserPresenceStatus, AvatarDirection, AvatarAnimState } from "realtime-contracts";
import { getMapLayout } from "../../spatial/MapRegistry";
import { compileLayout } from "../../spatial/LayoutCompiler";
import { getRoomAtTile, type ActiveRoom } from "../../spatial/roomDetection";
import "./auth-styles.css";
import FloatingHeader from "./components/FloatingHeader";
import Sidebar from "./components/Sidebar";
import BottomDock from "./components/BottomDock";

const SpatialEngineComponent = lazy(() => import("../../spatial/SpatialEngineComponent"));

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

interface UserProfile {
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
}

interface WorkspaceItem {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface SessionData {
  user_id: string;
  email: string;
  email_verified: boolean;
  profile: UserProfile;
  workspaces: WorkspaceItem[];
}

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Workspace creation form states
  const [showCreateWs, setShowCreateWs] = useState(false);
  const [newWsName, setNewWsName] = useState("");
  const [wsLoading, setWsLoading] = useState(false);
  const [wsError, setWsError] = useState<string | null>(null);

  // Active map layout configuration
  const activeLayout = useMemo(
    () => getMapLayout(selectedWorkspace?.slug || selectedWorkspace?.name),
    [selectedWorkspace]
  );
  const compiledRooms = useMemo(() => compileLayout(activeLayout).rooms, [activeLayout]);
  const initialSpawnRoom = useMemo(() => {
    return getRoomAtTile(activeLayout.spawnPoint.x, activeLayout.spawnPoint.y, compiledRooms);
  }, [activeLayout, compiledRooms]);

  // Realtime state parameters
  const [socketStatus, setSocketStatus] = useState<"connected" | "reconnecting" | "disconnected">("disconnected");
  const [userStatus, setUserStatus] = useState<UserPresenceStatus>("online");
  const [playerPos, setPlayerPos] = useState({
    x: activeLayout.spawnPoint.x,
    y: activeLayout.spawnPoint.y,
    direction: "down" as AvatarDirection,
    animState: "idle" as AvatarAnimState,
  });
  const [currentRoom, setCurrentRoom] = useState<ActiveRoom | null>(initialSpawnRoom);
  const [remoteUsers, setRemoteUsers] = useState<Record<string, PresencePayload>>({});
  const [isSpatialEnabled] = useState(true);
  const [isPresenceOpen, setIsPresenceOpen] = useState(true);

  // Realtime refs
  const presenceChannelRef = useRef<any>(null);
  const movementChannelRef = useRef<any>(null);
  const lastBroadcastRef = useRef<number>(0);
  const sessionTokenRef = useRef<string | null>(null);

  const playerPosRef = useRef(playerPos);
  const userStatusRef = useRef(userStatus);
  const currentRoomRef = useRef<ActiveRoom | null>(initialSpawnRoom);

  useEffect(() => {
    playerPosRef.current = playerPos;
  }, [playerPos]);
  useEffect(() => {
    userStatusRef.current = userStatus;
  }, [userStatus]);
  useEffect(() => {
    currentRoomRef.current = currentRoom;
  }, [currentRoom]);

  // Sync initial spawn point and room when workspace/layout changes
  useEffect(() => {
    const spawnRoom = getRoomAtTile(activeLayout.spawnPoint.x, activeLayout.spawnPoint.y, compiledRooms);
    setPlayerPos({
      x: activeLayout.spawnPoint.x,
      y: activeLayout.spawnPoint.y,
      direction: "down",
      animState: "idle",
    });
    setCurrentRoom(spawnRoom);
    currentRoomRef.current = spawnRoom;
    setRemoteUsers({});
  }, [activeLayout, compiledRooms]);

  useEffect(() => {
    if (loading) return;

    if (selectedWorkspace) {
      localStorage.setItem("last_workspace_slug", selectedWorkspace.slug);
      const currentSlug = window.location.pathname.split("/w/")[1];
      if (currentSlug !== selectedWorkspace.slug) {
        window.history.pushState(null, "", `/w/${selectedWorkspace.slug}`);
      }
    } else {
      localStorage.removeItem("last_workspace_slug");
      if (window.location.pathname !== "/coming-soon") {
        window.history.pushState(null, "", "/coming-soon");
      }
    }
  }, [selectedWorkspace, loading]);

  async function checkAuthAndFetchSession() {
    try {
      const hasHashToken = window.location.hash.includes("access_token=") || window.location.hash.includes("id_token=");
      if (hasHashToken) {
        for (let i = 0; i < 15; i++) {
          const tempSession = await getActiveSession();
          if (tempSession) break;
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      const session = await getActiveSession();
      if (!session) {
        window.location.href = "/signin";
        return;
      }

      sessionTokenRef.current = session.access_token;
      const user = await getActiveUser();
      if (!user) {
        window.location.href = "/signin";
        return;
      }

      const isDev = import.meta.env.DEV;
      if (!isDev && !user.email_confirmed_at) {
        window.location.href = `/verify?email=${encodeURIComponent(user.email || "")}`;
        return;
      }

      const devHeaders: Record<string, string> = {
        Authorization: `Bearer ${session.access_token}`,
      };
      if (isDev) {
        const bypassStr = localStorage.getItem("dev_bypass_user");
        if (bypassStr) {
          const devUser = JSON.parse(bypassStr);
          devHeaders["x-user-id"] = devUser.id;
          devHeaders["x-user-email"] = devUser.email;
        }
      }

      const res = await fetch(`${API_URL}/v1/session`, {
        headers: devHeaders,
      });

      if (!res.ok) {
        if (res.status === 401) {
          window.location.href = "/signin";
          return;
        }
        if (res.status === 403) {
          window.location.href = `/verify?email=${encodeURIComponent(user.email || "")}`;
          return;
        }
        throw new Error(`Failed to fetch backend session: HTTP ${res.status}`);
      }

      const data: SessionData = await res.json();
      setSessionData(data);
      const fetchedWorkspaces = data.workspaces || [];
      setWorkspaces(fetchedWorkspaces);

      let initialSlug = "";
      if (window.location.pathname.startsWith("/w/")) {
        initialSlug = window.location.pathname.split("/w/")[1];
      } else {
        initialSlug = localStorage.getItem("last_workspace_slug") || "";
      }

      if (initialSlug) {
        const matched = fetchedWorkspaces.find((w) => w.slug === initialSlug);
        if (matched) {
          setSelectedWorkspace(matched);
        }
      }
    } catch (err: any) {
      console.error("Dashboard initialization failed:", err);
      setError(err.message || "Failed to establish authenticated session.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    checkAuthAndFetchSession();
  }, []);

  // Set up Supabase Realtime when a workspace is selected
  useEffect(() => {
    if (!selectedWorkspace || !sessionData) return;

    let active = true;
    const workspaceId = selectedWorkspace.id;
    const userId = sessionData.user_id;
    const email = sessionData.email;
    const displayName = sessionData.profile.display_name || email.split("@")[0];
    const avatarUrl = sessionData.profile.avatar_url;

    // 1. Subscribe to Presence
    const presenceChannel = supabase.channel(`workspace:${workspaceId}:presence`, {
      config: {
        presence: {
          key: userId,
        },
      },
    });

    presenceChannelRef.current = presenceChannel;

    const trackPresence = async (currX: number, currY: number, currStatus: UserPresenceStatus) => {
      try {
        await presenceChannel.track({
          userId,
          email,
          displayName,
          avatarUrl,
          status: currStatus,
          room: currentRoomRef.current ? currentRoomRef.current.id : null,
          x: currX,
          y: currY,
          lastActive: Date.now(),
        });
      } catch (err) {
        console.error("Failed to track presence:", err);
      }
    };

    presenceChannel
      .on("presence", { event: "sync" }, () => {
        if (!active) return;
        const state = presenceChannel.presenceState();
        const mappedUsers: Record<string, PresencePayload> = {};

        Object.keys(state).forEach((key) => {
          if (state[key] && state[key].length > 0) {
            const pres = state[key][0] as any;
            mappedUsers[key] = {
              userId: pres.userId || key,
              email: pres.email || "",
              displayName: pres.displayName || "Workspace Member",
              avatarUrl: pres.avatarUrl || null,
              status: pres.status || "online",
              room: pres.room || null,
              x: pres.x ?? 0,
              y: pres.y ?? 0,
              lastActive: pres.lastActive || Date.now(),
            };
          }
        });

        // Filter out own presence node in remote list
        delete mappedUsers[userId];
        setRemoteUsers(mappedUsers);
      })
      .subscribe((status) => {
        if (!active) return;
        if (status === "SUBSCRIBED") {
          setSocketStatus("connected");
          trackPresence(playerPosRef.current.x, playerPosRef.current.y, userStatusRef.current);
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          setSocketStatus("disconnected");
        }
      });

    // 2. Subscribe to Broadcast Channel for high-frequency movement coordinates
    const movementChannel = supabase.channel(`workspace:${workspaceId}:movement`);
    movementChannelRef.current = movementChannel;

    movementChannel
      .on("broadcast", { event: "move" }, ({ payload }) => {
        if (!active) return;
        const moveData = payload as MovementPayload;
        if (moveData.userId === userId) return;

        setRemoteUsers((prev) => {
          const user = prev[moveData.userId];
          if (!user) return prev;
          return {
            ...prev,
            [moveData.userId]: {
              ...user,
              x: moveData.x,
              y: moveData.y,
              lastActive: moveData.timestamp,
            },
          };
        });
      })
      .subscribe();

    return () => {
      active = false;
      presenceChannel.unsubscribe();
      movementChannel.unsubscribe();
      presenceChannelRef.current = null;
      movementChannelRef.current = null;
    };
  }, [selectedWorkspace, sessionData]);

  // Movement broadcast handler
  const movePlayer = useCallback((newX: number, newY: number) => {
    if (newX < 0 || newX >= activeLayout.width || newY < 0 || newY >= activeLayout.height) return;

    const currPos = playerPosRef.current;
    let dir: AvatarDirection = "down";
    if (newX > currPos.x) dir = "right";
    else if (newX < currPos.x) dir = "left";
    else if (newY > currPos.y) dir = "down";
    else if (newY < currPos.y) dir = "up";

    setPlayerPos({ x: newX, y: newY, direction: dir, animState: "walking" });

    const now = Date.now();
    const movementChannel = movementChannelRef.current;
    const presenceChannel = presenceChannelRef.current;

    if (movementChannel && sessionData) {
      movementChannel.send({
        type: "broadcast",
        event: "move",
        payload: {
          userId: sessionData.user_id,
          x: newX,
          y: newY,
          direction: dir,
          animState: "walking",
          timestamp: now,
        },
      });
    }

    // Throttle presence tracking updates to 2 Hz
    if (now - lastBroadcastRef.current >= 500) {
      lastBroadcastRef.current = now;
      if (presenceChannel && sessionData) {
        presenceChannel.track({
          userId: sessionData.user_id,
          email: sessionData.email,
          displayName: sessionData.profile.display_name || sessionData.email.split("@")[0],
          avatarUrl: sessionData.profile.avatar_url,
          status: userStatusRef.current,
          room: currentRoomRef.current ? currentRoomRef.current.id : null,
          x: newX,
          y: newY,
          lastActive: now,
        });
      }
    }
  }, [sessionData, activeLayout]);

  // Handler for when avatar stops moving (ensures final stopped state and coordinates are broadcast)
  const handlePlayerStop = useCallback((finalX: number, finalY: number) => {
    setPlayerPos((prev) => ({ ...prev, x: finalX, y: finalY, animState: "idle" }));

    const now = Date.now();
    const movementChannel = movementChannelRef.current;
    const presenceChannel = presenceChannelRef.current;

    if (movementChannel && sessionData) {
      movementChannel.send({
        type: "broadcast",
        event: "move",
        payload: {
          userId: sessionData.user_id,
          x: finalX,
          y: finalY,
          direction: playerPosRef.current.direction,
          animState: "idle",
          timestamp: now,
        },
      });
    }

    if (presenceChannel && sessionData) {
      presenceChannel.track({
        userId: sessionData.user_id,
        email: sessionData.email,
        displayName: sessionData.profile.display_name || sessionData.email.split("@")[0],
        avatarUrl: sessionData.profile.avatar_url,
        status: userStatusRef.current,
        room: currentRoomRef.current ? currentRoomRef.current.id : null,
        x: finalX,
        y: finalY,
        lastActive: now,
      });
    }
  }, [sessionData]);

  // Room transition handler: synchronizes room entry/exit to presence channel
  const handleRoomChange = useCallback((room: ActiveRoom | null) => {
    setCurrentRoom(room);
    currentRoomRef.current = room;

    const now = Date.now();
    const presenceChannel = presenceChannelRef.current;

    if (presenceChannel && sessionData) {
      presenceChannel.track({
        userId: sessionData.user_id,
        email: sessionData.email,
        displayName: sessionData.profile.display_name || sessionData.email.split("@")[0],
        avatarUrl: sessionData.profile.avatar_url,
        status: userStatusRef.current,
        room: room ? room.id : null,
        x: playerPosRef.current.x,
        y: playerPosRef.current.y,
        lastActive: now,
      });
    }
  }, [sessionData]);

  // Workspace creation submit handler
  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWsName.trim()) return;

    setWsLoading(true);
    setWsError(null);

    try {
      const token = sessionTokenRef.current;
      const isDev = import.meta.env.DEV;
      const devHeaders: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        devHeaders["Authorization"] = `Bearer ${token}`;
      }
      if (isDev) {
        const bypassStr = localStorage.getItem("dev_bypass_user");
        if (bypassStr) {
          const devUser = JSON.parse(bypassStr);
          devHeaders["x-user-id"] = devUser.id;
          devHeaders["x-user-email"] = devUser.email;
        }
      }

      const res = await fetch(`${API_URL}/v1/workspaces`, {
        method: "POST",
        headers: devHeaders,
        body: JSON.stringify({ name: newWsName.trim() }),
      });

      if (!res.ok) {
        throw new Error(`Failed to create workspace: HTTP ${res.status}`);
      }

      const newWs = await res.json();
      const mappedWs: WorkspaceItem = {
        id: newWs.id,
        name: newWs.name,
        slug: newWs.slug,
        role: "owner",
      };

      setWorkspaces((prev) => [...prev, mappedWs]);
      setSelectedWorkspace(mappedWs);
      setNewWsName("");
      setShowCreateWs(false);
    } catch (err: any) {
      console.error(err);
      setWsError(err.message || "Failed to create workspace. Try another name.");
    } finally {
      setWsLoading(false);
    }
  };

  // Status indicator switch handler
  const handleStatusChange = async (newStatus: UserPresenceStatus) => {
    setUserStatus(newStatus);
    const presenceChannel = presenceChannelRef.current;
    if (presenceChannel && sessionData) {
      try {
        await presenceChannel.track({
          userId: sessionData.user_id,
          email: sessionData.email,
          displayName: sessionData.profile.display_name || sessionData.email.split("@")[0],
          avatarUrl: sessionData.profile.avatar_url,
          status: newStatus,
          room: currentRoomRef.current ? currentRoomRef.current.id : null,
          x: playerPosRef.current.x,
          y: playerPosRef.current.y,
          lastActive: Date.now(),
        });
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      window.location.href = "/signin";
    } catch (err: any) {
      console.error("Logout failed:", err);
      alert("Failed to logout. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: "center" }}>
          <h2 style={{ fontFamily: "Fraunces, serif" }}>Verifying Session...</h2>
          <div style={{ marginTop: "1rem", color: "#666" }}>Please wait while we connect to the server.</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: "center" }}>
          <h2 style={{ fontFamily: "Fraunces, serif", color: "#721C24" }}>Connection Error</h2>
          <div className="error-banner" style={{ marginTop: "1rem" }}>{error}</div>
          <button className="auth-btn auth-btn-primary" onClick={handleLogout}>
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // SCREEN A: Workspace Switcher / Creation View
  if (!selectedWorkspace) {
    return (
      <div className="auth-container">
        <div className="ambient-bg">
          <div className="blob blob-1"></div>
          <div className="blob blob-2"></div>
        </div>

        <div className="auth-card ws-selector-card">
          <div className="auth-header">
            <h1 className="auth-logo">
              Huddlezen
              <span className="logo-dot"></span>
            </h1>
            <p className="auth-subtitle">Select or create a workspace to enter</p>
          </div>

          {showCreateWs ? (
            <form onSubmit={handleCreateWorkspace} style={{ marginTop: "1rem" }}>
              <h3 style={{ fontFamily: "Fraunces, serif", marginBottom: "0.5rem" }}>Create New Workspace</h3>
              {wsError && <div className="error-banner">{wsError}</div>}

              <div className="form-group">
                <label className="form-label" htmlFor="ws-name">Workspace Name</label>
                <input
                  className="form-input"
                  id="ws-name"
                  type="text"
                  placeholder="e.g. Acme Marketing"
                  value={newWsName}
                  onChange={(e) => setNewWsName(e.target.value)}
                  disabled={wsLoading}
                  required
                />
              </div>

              <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                <button className="auth-btn auth-btn-primary" type="submit" disabled={wsLoading}>
                  {wsLoading ? "Creating..." : "Create & Enter"}
                </button>
                <button
                  className="auth-btn"
                  type="button"
                  onClick={() => { setShowCreateWs(false); setWsError(null); }}
                  disabled={wsLoading}
                  style={{ background: "#F5F5F5", border: "1px solid rgba(0,0,0,0.15)" }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="ws-list">
                {workspaces.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "2rem", color: "#666", fontSize: "0.95rem" }}>
                    You don't belong to any workspaces yet.
                  </div>
                ) : (
                  workspaces.map((ws) => (
                    <div key={ws.id} className="ws-item" onClick={() => setSelectedWorkspace(ws)}>
                      <div>
                        <strong style={{ fontSize: "1.1rem" }}>{ws.name}</strong>
                        <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "0.2rem" }}>/{ws.slug}</div>
                      </div>
                      <span style={{ fontSize: "0.75rem", background: "rgba(192, 138, 110, 0.15)", padding: "0.25rem 0.5rem", borderRadius: "12px", textTransform: "capitalize", color: "#8c563b", fontWeight: 600 }}>
                        {ws.role}
                      </span>
                    </div>
                  ))
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
                <button className="auth-btn auth-btn-primary" onClick={() => setShowCreateWs(true)}>
                  + Create New Workspace
                </button>
                <button className="auth-btn" onClick={handleLogout} style={{ background: "#FFFFFF", border: "1px solid rgba(26, 26, 26, 0.15)" }}>
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // SCREEN B: Realtime Presence & Movement Interactive Grid
  const gridRows = Array.from({ length: 10 }, (_, i) => i);
  const gridCols = Array.from({ length: 10 }, (_, i) => i);

  // Helper to extract initials
  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="spatial-viewport">
      {/* Immersive background spatial canvas */}
      <div className="spatial-canvas-container">
        {isSpatialEnabled ? (
          <Suspense fallback={<div style={{ padding: "2rem", color: "#666", textAlign: "center", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading Spatial Office...</div>}>
            <SpatialEngineComponent
              initialX={activeLayout.spawnPoint.x}
              initialY={activeLayout.spawnPoint.y}
              onPlayerMove={movePlayer}
              onPlayerStop={handlePlayerStop}
              onRoomChange={handleRoomChange}
              remoteUsers={remoteUsers}
              isPresenceOpen={isPresenceOpen}
              workspaceSlug={selectedWorkspace?.slug || selectedWorkspace?.name}
            />
          </Suspense>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", padding: "6rem 2rem 2rem 2rem", boxSizing: "border-box" }}>
            <div className="coordinate-grid" style={{ margin: 0 }}>
              {gridRows.map((row) =>
                gridCols.map((col) => {
                  const isLocal = playerPos.x === col && playerPos.y === row;
                  const remUser = Object.values(remoteUsers).find((u) => u.x === col && u.y === row);

                  return (
                    <div key={`${col}-${row}`} className="grid-cell" onClick={() => movePlayer(col, row)}>
                      <div style={{ fontSize: "0.6rem", color: "#ccc", position: "absolute", top: 2, left: 2, userSelect: "none" }}>
                        {col},{row}
                      </div>

                      {isLocal && (
                        <div className="avatar-node local" title="You">
                          {getInitials(sessionData?.profile.display_name || sessionData?.email || "ME")}
                        </div>
                      )}

                      {remUser && !isLocal && (
                        <div
                          className={`avatar-node remote-${remUser.status}`}
                          title={`${remUser.displayName} (${remUser.status})`}
                        >
                          {getInitials(remUser.displayName)}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floating HUD Controls */}
      <FloatingHeader
        selectedWorkspace={selectedWorkspace}
        displayName={sessionData?.profile.display_name || sessionData?.email.split("@")[0] || "User"}
        userStatus={userStatus as any}
        onStatusChange={(status) => handleStatusChange(status as any)}
        onExitWorkspace={() => setSelectedWorkspace(null)}
        onSignOut={handleLogout}
      />

      <Sidebar
        isOpen={isPresenceOpen}
        workspaces={workspaces}
        selectedWorkspace={selectedWorkspace}
        onSelectWorkspace={(ws) => setSelectedWorkspace(ws)}
        userStatus={userStatus as any}
        localPlayerPos={playerPos}
        currentRoom={currentRoom}
        displayName={sessionData?.profile.display_name || sessionData?.email.split("@")[0] || "User"}
        remoteUsers={remoteUsers as any}
        onExitWorkspace={() => setSelectedWorkspace(null)}
        onSignOut={handleLogout}
      />

      <BottomDock
        isPresenceOpen={isPresenceOpen}
        onTogglePresence={() => setIsPresenceOpen(prev => !prev)}
        socketStatus={socketStatus}
      />
    </div>
  );
}
